import React, { useMemo } from "react";
import { Theme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  DriveFolderUpload as DriveFolderUploadIcon,
  Description as DescriptionIcon,
  Campaign as CampaignIcon,
  CalendarMonth as CalendarMonthIcon,
  Event as EventIcon,
  Groups as GroupsIcon,
  People as PeopleIcon,
  Send as SendIcon,
  ChatBubbleOutline as ChatBubbleOutlineIcon,
  Image as ImageIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";
import {
  DESKTOP_CHART_LAYOUT,
  DESKTOP_HERO_LAYOUT,
  DashboardActionFeatureFlag,
  DashboardActionId,
  MOBILE_DRAWER_LAYOUT,
  MOBILE_HERO_LAYOUT,
  MOBILE_HOME_LAYOUT,
} from "../constants/dashboardQuickActions";

export interface DashboardQuickActionHandlers {
  handleCreateNotice: () => void;
  handleOpenInfoNoticeModal: () => void;
  handleOpenSendImageModal: () => void;
  handleOpenSendAttachmentModal: () => void;
  handleViewNotices: () => void;
  handleCirclesManagement: () => void;
  handleUserManagement: () => void;
  handleOpenDriveUpload: () => void;
  handleOpenDirectNoticeLauncher: () => void;
  handleOpenAddEventModal: () => void;
  navigateToCalendar: () => void;
  navigateToTeamManagement: () => void;
}

export interface DashboardQuickActionFlags {
  isCircleManagementEnabled: boolean;
  isUserManagementEnabled: boolean;
  isTeamManagementEnabled: boolean;
  isMobileDashboard: boolean;
}

interface UseDashboardQuickActionsParams {
  theme: Theme;
  handlers: DashboardQuickActionHandlers;
  flags: DashboardQuickActionFlags;
}

interface ActionVisual {
  icon: React.ReactNode;
  iconColor: string;
  iconBg: string;
}

/**
 * Shared by mobileDrawerQuickActions and desktopChartQuickActions — the two
 * already agree on icon/color for every action. mobileHomeQuickActions has
 * its own catalog below since it genuinely differs for 4 actions
 * (send-notice, calendar, circles, add-users) — not unified here, since
 * reconciling those would be a visible design change, not a refactor.
 */
function useActionVisuals(theme: Theme): Record<DashboardActionId, ActionVisual> {
  return useMemo(
    () => ({
      "send-notice": {
        icon: <SendIcon />,
        iconColor: theme.palette.info.main,
        iconBg: alpha(theme.palette.info.main, 0.12),
      },
      "view-notices": {
        icon: <DescriptionIcon />,
        iconColor: theme.palette.custom.navNoticeFeed,
        iconBg: alpha(theme.palette.custom.navNoticeFeed, 0.12),
      },
      calendar: {
        icon: <CalendarMonthIcon />,
        iconColor: theme.palette.colors.green,
        iconBg: theme.palette.template.eventLight,
      },
      circles: {
        icon: <GroupsIcon />,
        iconColor: theme.palette.warning.main,
        iconBg: theme.palette.cards.orangeBg,
      },
      "add-users": {
        icon: <PeopleIcon />,
        iconColor: theme.palette.info.main,
        iconBg: alpha(theme.palette.info.main, 0.12),
      },
      "manage-team": {
        icon: <GroupsIcon />,
        iconColor: theme.palette.custom.navTeamManagement,
        iconBg: alpha(theme.palette.custom.navTeamManagement, 0.12),
      },
      "creators-hub": {
        icon: <CampaignIcon />,
        iconColor: theme.palette.info.main,
        iconBg: alpha(theme.palette.info.main, 0.12),
      },
      "send-image": {
        icon: <ImageIcon />,
        iconColor: "#fb7185",
        iconBg: "rgba(251, 113, 133, 0.12)",
      },
      attachment: {
        icon: <AttachFileIcon />,
        iconColor: theme.palette.warning.main,
        iconBg: theme.palette.cards.orangeBg,
      },
      "add-event": {
        icon: <EventIcon />,
        iconColor: theme.palette.colors.green,
        iconBg: theme.palette.template.eventLight,
      },
      "upload-drive": {
        icon: <DriveFolderUploadIcon />,
        iconColor: theme.palette.custom.navNoticeFeed,
        iconBg: alpha(theme.palette.custom.navNoticeFeed, 0.12),
      },
      "direct-message": {
        icon: <ChatBubbleOutlineIcon />,
        iconColor: theme.palette.warning.main,
        iconBg: theme.palette.cards.orangeBg,
      },
    }),
    [theme]
  );
}

const isFeatureEnabled = (
  flag: DashboardActionFeatureFlag | undefined,
  flags: DashboardQuickActionFlags
): boolean => {
  if (!flag) return true;
  switch (flag) {
    case "circleManagement":
      return flags.isCircleManagementEnabled;
    case "userManagement":
      return flags.isUserManagementEnabled;
    case "teamManagement":
      return flags.isTeamManagementEnabled;
    default:
      return true;
  }
};

export const useDashboardQuickActions = ({
  theme,
  handlers,
  flags,
}: UseDashboardQuickActionsParams) => {
  const quickActionIconSize = theme.spacing(3.5);
  const actionVisuals = useActionVisuals(theme);

  const heroQuickActions = useMemo(() => {
    const layout = flags.isMobileDashboard ? MOBILE_HERO_LAYOUT : DESKTOP_HERO_LAYOUT;
    const heroLabels: Record<DashboardActionId, string> = {
      "creators-hub": "Creators Hub",
      "send-notice": "Send Notice",
      "add-event": "Create an event",
      "upload-drive": "Upload to Drive",
      "direct-message": "Direct Message",
      "view-notices": "View Notices",
      calendar: "Calendar",
      circles: "Circles",
      "add-users": "Add Users",
      "manage-team": "Manage Team",
      "send-image": "Image",
      attachment: "Attachment",
    };

    const heroHandlers: Record<DashboardActionId, () => void> = {
      "creators-hub": handlers.handleCreateNotice,
      "send-notice": handlers.handleOpenInfoNoticeModal,
      "add-event": handlers.handleOpenAddEventModal,
      "upload-drive": handlers.handleOpenDriveUpload,
      "direct-message": handlers.handleOpenDirectNoticeLauncher,
      "view-notices": handlers.handleViewNotices,
      calendar: handlers.navigateToCalendar,
      circles: handlers.handleCirclesManagement,
      "add-users": handlers.handleUserManagement,
      "manage-team": handlers.navigateToTeamManagement,
      "send-image": handlers.handleOpenSendImageModal,
      attachment: handlers.handleOpenSendAttachmentModal,
    };

    return layout.map(({ id, variant }) => ({
      label: heroLabels[id],
      onClick: heroHandlers[id],
      variant,
    }));
  }, [flags.isMobileDashboard, handlers]);

  const mobileHomeQuickActions = useMemo(() => {
    type MobileHomeAction = {
      label: string;
      description: string;
      icon: React.ReactNode;
      iconColor: string;
      iconBg: string;
      onClick: () => void;
    };

    const catalog: Record<DashboardActionId, MobileHomeAction> = {
      "send-notice": {
        label: "Send a notice",
        description: "Send a notice in 10s",
        icon: <CampaignIcon sx={{ fontSize: quickActionIconSize }} />,
        iconColor: theme.palette.info.main,
        iconBg: alpha(theme.palette.info.main, 0.12),
        onClick: handlers.handleOpenInfoNoticeModal,
      },
      "view-notices": {
        label: "View Notices",
        description: "Browse and manage all notices",
        icon: <DescriptionIcon sx={{ fontSize: quickActionIconSize }} />,
        iconColor: theme.palette.custom.navNoticeFeed,
        iconBg: alpha(theme.palette.custom.navNoticeFeed, 0.12),
        onClick: handlers.handleViewNotices,
      },
      calendar: {
        label: "View Calendar",
        description: "See upcoming events and your schedule",
        icon: <CalendarMonthIcon sx={{ fontSize: quickActionIconSize }} />,
        iconColor: theme.palette.colors.green,
        iconBg: alpha(theme.palette.colors.green, 0.12),
        onClick: handlers.navigateToCalendar,
      },
      circles: {
        label: "Circles",
        description: "Manage your circles and structure",
        icon: <GroupsIcon sx={{ fontSize: quickActionIconSize }} />,
        iconColor: theme.palette.custom.navManageCircles,
        iconBg: alpha(theme.palette.custom.navManageCircles, 0.12),
        onClick: handlers.handleCirclesManagement,
      },
      "add-users": {
        label: "Add Users",
        description: "Invite and manage users",
        icon: <GroupsIcon sx={{ fontSize: quickActionIconSize }} />,
        iconColor: theme.palette.info.main,
        iconBg: alpha(theme.palette.info.main, 0.12),
        onClick: handlers.handleUserManagement,
      },
      "manage-team": {
        label: "Manage Team",
        description: "Add, edit or remove team members",
        icon: <GroupsIcon sx={{ fontSize: quickActionIconSize }} />,
        iconColor: theme.palette.custom.navTeamManagement,
        iconBg: alpha(theme.palette.custom.navTeamManagement, 0.12),
        onClick: handlers.navigateToTeamManagement,
      },
      "creators-hub": {
        label: "Creators Hub",
        description: "",
        icon: <CampaignIcon sx={{ fontSize: quickActionIconSize }} />,
        iconColor: theme.palette.info.main,
        iconBg: alpha(theme.palette.info.main, 0.12),
        onClick: handlers.handleCreateNotice,
      },
      "send-image": {
        label: "Image",
        description: "",
        icon: <ImageIcon sx={{ fontSize: quickActionIconSize }} />,
        iconColor: "#fb7185",
        iconBg: "rgba(251, 113, 133, 0.12)",
        onClick: handlers.handleOpenSendImageModal,
      },
      attachment: {
        label: "Attachment",
        description: "",
        icon: <AttachFileIcon sx={{ fontSize: quickActionIconSize }} />,
        iconColor: theme.palette.warning.main,
        iconBg: theme.palette.cards.orangeBg,
        onClick: handlers.handleOpenSendAttachmentModal,
      },
      "add-event": {
        label: "Add Event",
        description: "",
        icon: <EventIcon sx={{ fontSize: quickActionIconSize }} />,
        iconColor: theme.palette.colors.green,
        iconBg: theme.palette.template.eventLight,
        onClick: handlers.handleOpenAddEventModal,
      },
      "upload-drive": {
        label: "Upload",
        description: "",
        icon: <DriveFolderUploadIcon sx={{ fontSize: quickActionIconSize }} />,
        iconColor: theme.palette.custom.navNoticeFeed,
        iconBg: alpha(theme.palette.custom.navNoticeFeed, 0.12),
        onClick: handlers.handleOpenDriveUpload,
      },
      "direct-message": {
        label: "Direct Message",
        description: "",
        icon: <ChatBubbleOutlineIcon sx={{ fontSize: quickActionIconSize }} />,
        iconColor: theme.palette.warning.main,
        iconBg: theme.palette.cards.orangeBg,
        onClick: handlers.handleOpenDirectNoticeLauncher,
      },
    };

    return MOBILE_HOME_LAYOUT.filter((entry) =>
      isFeatureEnabled(entry.featureFlag, flags)
    ).map((entry) => catalog[entry.id]);
  }, [flags, handlers, quickActionIconSize, theme]);

  const mobileDrawerQuickActions = useMemo(() => {
    const labels: Record<DashboardActionId, string> = {
      "send-notice": "Send",
      "send-image": "Image",
      attachment: "Attachment",
      "add-event": "Add Event",
      "upload-drive": "Upload",
      "direct-message": "Direct Message",
      "creators-hub": "Creators Hub",
      "view-notices": "View Notices",
      calendar: "Calendar",
      circles: "Circles",
      "add-users": "Users",
      "manage-team": "Manage Team",
    };

    const descriptions: Partial<Record<DashboardActionId, string>> = {
      "send-notice": "Send a notice to your audience",
      "send-image": "Share a photo or image-first notice",
      attachment: "Send files and documents",
      "add-event": "Create and share an event",
      "upload-drive": "Upload files to your drive",
      "direct-message": "Send a direct message to a user or circle",
    };

    const onClicks: Record<DashboardActionId, () => void> = {
      "send-notice": handlers.handleOpenInfoNoticeModal,
      "send-image": handlers.handleOpenSendImageModal,
      attachment: handlers.handleOpenSendAttachmentModal,
      "add-event": handlers.handleOpenAddEventModal,
      "upload-drive": handlers.handleOpenDriveUpload,
      "direct-message": handlers.handleOpenDirectNoticeLauncher,
      "creators-hub": handlers.handleCreateNotice,
      "view-notices": handlers.handleViewNotices,
      calendar: handlers.navigateToCalendar,
      circles: handlers.handleCirclesManagement,
      "add-users": handlers.handleUserManagement,
      "manage-team": handlers.navigateToTeamManagement,
    };

    return MOBILE_DRAWER_LAYOUT.map(({ id }) => ({
      section: "create" as const,
      label: labels[id],
      description: descriptions[id] || "",
      ...actionVisuals[id],
      onClick: onClicks[id],
    }));
  }, [actionVisuals, handlers]);

  const desktopChartQuickActions = useMemo(() => {
    const labels: Record<DashboardActionId, string> = {
      "send-notice": "Send Notice",
      "view-notices": "View Notices",
      calendar: "Calendar",
      circles: "Circles",
      "add-users": "Users",
      "creators-hub": "Creators Hub",
      "send-image": "Image",
      attachment: "Attachment",
      "add-event": "Add Event",
      "upload-drive": "Upload",
      "direct-message": "Direct Message",
      "manage-team": "Manage Team",
    };

    const onClicks: Record<DashboardActionId, () => void> = {
      "send-notice": handlers.handleOpenInfoNoticeModal,
      "view-notices": handlers.handleViewNotices,
      calendar: handlers.navigateToCalendar,
      circles: handlers.handleCirclesManagement,
      "add-users": handlers.handleUserManagement,
      "creators-hub": handlers.handleCreateNotice,
      "send-image": handlers.handleOpenSendImageModal,
      attachment: handlers.handleOpenSendAttachmentModal,
      "add-event": handlers.handleOpenAddEventModal,
      "upload-drive": handlers.handleOpenDriveUpload,
      "direct-message": handlers.handleOpenDirectNoticeLauncher,
      "manage-team": handlers.navigateToTeamManagement,
    };

    return DESKTOP_CHART_LAYOUT.filter((entry) =>
      isFeatureEnabled(entry.featureFlag, flags)
    ).map(({ id }) => ({
      label: labels[id],
      ...actionVisuals[id],
      onClick: onClicks[id],
    }));
  }, [actionVisuals, flags, handlers]);

  return {
    heroQuickActions,
    mobileHomeQuickActions,
    mobileDrawerQuickActions,
    desktopChartQuickActions,
  };
};
